import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CatalogueAdsResponseModel, ParamsToGetAdsList } from '../digital-catalogue.model';
import { Router } from '@angular/router';
import { AuthBllService } from '../../auth/auth-bll.service';
import { APIErrorModel } from '../../../shared/models/base-response.model';
import { DigitalCatalogueBllService } from '../digital-catalogue-bll.service';
import { Subscription } from 'rxjs';
import { DeviceTypeService } from '../../../core/services/device-type.service';
declare var bootstrap: any;

@Component({
  selector: 'app-catalogue-ads',
  imports: [CommonModule],
  templateUrl: './catalogue-ads.component.html',
  styleUrl: './catalogue-ads.component.scss'
})
export class CatalogueAdsComponent implements AfterViewInit, OnInit, OnChanges, OnDestroy {

  // carousel reference
  @ViewChild('carousel', { static: false }) carouselRef!: ElementRef;
  private carouselInstance: any;

  // Input for pavilion code
  @Input() adsPavilionCode: string = '';

  // Authentication status
  isAuthenticated: boolean = false;

  // Ads variables
  paramsToFetchData!: ParamsToGetAdsList;
  adsList: CatalogueAdsResponseModel[] = [];
  isAdsLoading: boolean = false;

  // Subscription for authentication status
  private authSubscription?: Subscription;

  // variable to hold the source of user(android_web/ios_web/web)
  source: string = "";

  private viewedSlides = new Set<number>();

  constructor(
    private router: Router,
    private authService: AuthBllService,
    private catalogueBllService: DigitalCatalogueBllService,
    private deviceService: DeviceTypeService
  ) { }

  ngOnInit(): void {
    this.authSubscription = this.authService.isAuthenticated$.subscribe((isAuthenticated) => {
      this.isAuthenticated = isAuthenticated;
    });

    // Get the source of user
    this.source = this.deviceService.getUserSource();
  }

  ngAfterViewInit(): void {
    // Initialize the carousel first time
    this.initializeCarousel();
  }

  // Initialize Bootstrap Carousel
  private initializeCarousel(): void {
    setTimeout(() => {
      const carouselEl = this.carouselRef?.nativeElement;
      if (carouselEl) {
        // Dispose any old instance
        const existing = bootstrap.Carousel.getInstance(carouselEl);
        if (existing) existing.dispose();

        // Create a new one
        this.carouselInstance = new bootstrap.Carousel(carouselEl, {
          interval: 4000,
          ride: 'carousel',
          pause: 'hover',
          wrap: true
        });

        // Trigger FIRST SLIDE (index 0) manually
        setTimeout(() => {
          if (this.adsList.length > 0 && !this.viewedSlides.has(0)) {
            this.viewedSlides.add(0);
            this.logViewActivity(this.adsList[0]);
          }
        });

        // Listen when slide becomes visible
        carouselEl.addEventListener('slid.bs.carousel', (event: any) => {
          const index = event.to; // current active slide
          const ad = this.adsList[index];
          // BLOCK REPEAT CALLS
          if (!this.viewedSlides.has(index)) {
            this.viewedSlides.add(index);   // Mark slide as viewed
            this.logViewActivity(ad);       // Call your API only once
          }
        });
      }
    });
  }

  private logViewActivity(ad: CatalogueAdsResponseModel) {
    if (!ad) return;
    // Only for authenticated users
    if (!this.isAuthenticated) return;

    this.callAdsActivity(ad.id, 'view_ad');
  }

  // Detect changes to pavilion code
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['adsPavilionCode'] && this.adsPavilionCode) {

      // BUILD THE PARAMS HERE (after Input is available)
      this.paramsToFetchData = {
        ad_position: 'topslider',
        ad_screen: 'digital_catalogue',
        pavilion_code: this.adsPavilionCode
      };

      // Now call API
      this.callFetchAds(this.paramsToFetchData);
    }
  }

  /**
   * Fetch ads from API
   * @param params 
   */
  callFetchAds(params?: ParamsToGetAdsList) {
    this.isAdsLoading = true;
    this.catalogueBllService
      .fetchAds(params)
      .then((res) => {
        this.adsList = res.data;
        this.isAdsLoading = false;

        // Initialize only once, after ads loaded
        this.initializeCarousel();
      })
      .catch((err: APIErrorModel) => {
        if (err.status === 404) {
          this.isAdsLoading = false;
        }
      });
  }

  // Handle ad click based on its type
  handleAdClick(ad: CatalogueAdsResponseModel) {

    if(this.isAuthenticated){
      // Log the click activity
      this.callAdsActivity(ad.id , 'click_ad');
    }
    
    if (ad.link_type === 'internal' && ad.link_for === 'Product') {
      if (!ad.linked_item_id) return;
      const encodedId = btoa(ad.linked_item_id.toString());

      if (!this.isAuthenticated) {
        this.authService.saveStorageValue("state", `/catalogue/product-details/${encodedId}`);
        this.router.navigate(["/auth/init"]);
      } else {
        this.router.navigate(["/catalogue/product-details", encodedId]);
      }

    }
    else if (ad.link_type === 'internal' && ad.link_for === 'Brand') {
      if (!ad.linked_item_id) return;
      const encodedId = btoa(ad.linked_item_id.toString()); // encode as Base64

      if (!this.isAuthenticated) {
        this.authService.saveStorageValue("state", `/catalogue/company-details?id=${encodedId}&userId=${ad.linked_item_id}`);
        this.router.navigate(["/auth/init"]);
      } else {
        // Navigate to the company details page using the company ID
        this.router.navigate(["catalogue/company-details"], {
          queryParams: {
            id: encodedId,
            userId: ad.linked_item_id,
          },
        });
      }
    }
    else {
      this.openMedia(ad.media_url);
    }
  }

  // Open media URL in a new tab
  openMedia(url: string): void {
    window.open(url, '_blank');
  }

  ngOnDestroy(): void {
    this.carouselInstance?.dispose?.();
    this.authSubscription?.unsubscribe();
  }

  /**
  * @description call ads activity API
  * @param adId The ID of the ad.
  * @param activityCode The activity code (e.g., 'view_ad', 'click_ad').
  */
  callAdsActivity(adId: number, activityCode: string): void {
    const activityRequest = {
      activity_code: activityCode,
      activity_for_item_id: adId,
      source: this.source,
    };

    this.catalogueBllService.submitAdsActivity(activityRequest)
      .then((res) => {
        // No further action needed as we just log the activity
      }).catch((err) => {
        console.log("Error submitting ad activity:", err);
      });
  }

}
